import { useEffect, useRef, useState } from 'react';
import { useToast } from './Toast';
import {
  fetchBannerPresetsAdmin,
  createBannerPreset,
  updateBannerPreset,
  deleteBannerPreset,
  uploadBannerPresetImage,
  type BannerPresetRow,
} from '../lib/admin-queries';

/**
 * Admin -> Banners. Manages the catalog of banner images members can
 * pre-select on their profile (the `banner_presets` table). Rendered inside
 * a panel in Admin.tsx; role-gating is handled by the parent.
 */
export function AdminBanners({ active, createdBy }: { active: boolean; createdBy: string }) {
  const toast = useToast();
  const [presets, setPresets] = useState<BannerPresetRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  // Lazy-load the first time the panel is opened.
  useEffect(() => {
    if (active && !loaded) void load();
  }, [active, loaded]);

  async function load() {
    setLoading(true);
    try {
      setPresets(await fetchBannerPresetsAdmin());
      setLoaded(true);
    } catch (err: any) {
      console.error('[admin/banners] load failed', err);
      toast.error(err?.message ?? 'Failed to load banners.');
    } finally {
      setLoading(false);
    }
  }

  async function handleUpload(file: File | null) {
    if (!file) return;
    setUploading(true);
    try {
      const { image_url, thumb_url } = await uploadBannerPresetImage(file);
      const nextOrder = presets.reduce((m, p) => Math.max(m, p.sort_order), 0) + 1;
      const created = await createBannerPreset({
        label: null,
        image_url,
        thumb_url,
        sort_order: nextOrder,
        created_by: createdBy,
      });
      setPresets((cur) => [...cur, created]);
      toast.success('Banner added.');
    } catch (err: any) {
      console.error('[admin/banners] upload failed', err);
      toast.error(err?.message ?? 'Upload failed.');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function saveLabel(p: BannerPresetRow, raw: string) {
    const label = raw.trim() || null;
    if (label === (p.label ?? null)) return;
    try {
      await updateBannerPreset(p.id, { label });
      setPresets((cur) => cur.map((x) => (x.id === p.id ? { ...x, label } : x)));
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not save label.');
    }
  }

  async function toggleActive(p: BannerPresetRow) {
    setBusyId(p.id);
    try {
      await updateBannerPreset(p.id, { is_active: !p.is_active });
      setPresets((cur) => cur.map((x) => (x.id === p.id ? { ...x, is_active: !x.is_active } : x)));
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not update.');
    } finally {
      setBusyId(null);
    }
  }

  async function move(p: BannerPresetRow, dir: -1 | 1) {
    const idx = presets.findIndex((x) => x.id === p.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= presets.length) return;
    const other = presets[swap];
    setBusyId(p.id);
    try {
      await updateBannerPreset(p.id, { sort_order: other.sort_order });
      await updateBannerPreset(other.id, { sort_order: p.sort_order });
      setPresets((cur) => {
        const next = [...cur];
        next[idx] = { ...other, sort_order: p.sort_order };
        next[swap] = { ...p, sort_order: other.sort_order };
        return next;
      });
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not reorder.');
    } finally {
      setBusyId(null);
    }
  }

  async function remove(p: BannerPresetRow) {
    if (!window.confirm('Delete this banner permanently? Members who already chose it will lose it. To just stop offering it, use Hide instead.')) return;
    setBusyId(p.id);
    try {
      await deleteBannerPreset(p);
      setPresets((cur) => cur.filter((x) => x.id !== p.id));
      toast.success('Banner deleted.');
    } catch (err: any) {
      toast.error(err?.message ?? 'Could not delete.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="admin-banners">
      <div className="admin__panel-head">
        <h2>Banners</h2>
        <div className="admin-banners__add">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="visually-hidden"
            onChange={(e) => handleUpload(e.target.files?.[0] ?? null)}
          />
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? 'Uploading…' : '+ Add banner'}
          </button>
        </div>
      </div>

      <p className="admin-banners__intro">
        These are the banners members can pre-select on their profile. Add new ones,
        reorder them with the arrows, or hide ones you no longer want offered. Hiding
        keeps the banner on any profile already using it; Delete removes it everywhere.
      </p>

      {loading ? (
        <p className="admin-banners__muted">Loading…</p>
      ) : presets.length === 0 ? (
        <p className="admin-banners__muted">No banners yet. Add one above.</p>
      ) : (
        <div className="admin-banners__grid">
          {presets.map((p, i) => (
            <div key={p.id} className={'admin-banner-card' + (p.is_active ? '' : ' is-inactive')}>
              <img
                className="admin-banner-card__img"
                src={p.thumb_url || p.image_url}
                alt={p.label || 'Banner preset'}
                loading="lazy"
              />
              <div className="admin-banner-card__body">
                <input
                  type="text"
                  defaultValue={p.label ?? ''}
                  placeholder={`Banner ${i + 1}`}
                  onBlur={(e) => saveLabel(p, e.target.value)}
                  aria-label="Banner label"
                />
                <div className="admin-banner-card__row">
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busyId === p.id || i === 0} onClick={() => move(p, -1)} title="Move up">↑</button>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busyId === p.id || i === presets.length - 1} onClick={() => move(p, 1)} title="Move down">↓</button>
                  <button type="button" className="btn btn--secondary btn--sm" disabled={busyId === p.id} onClick={() => toggleActive(p)}>
                    {p.is_active ? 'Hide' : 'Show'}
                  </button>
                  <button type="button" className="btn btn--ghost btn--sm" disabled={busyId === p.id} onClick={() => remove(p)}>Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
